// @givmo/app-admin — the three SERVER-rendered CSV exports (EP-WEB7).
//
// Transactions, tax, and impact are rendered by the backend and streamed as a binary body; the client
// only opts out of JSON parsing and saves the blob. That is the opposite of `enrollment-export.ts`,
// which pages the roster and writes the CSV itself — see `csv-export.ts` for why the two differ.
//
// **Each of these is server-side for a reason that is not convenience.** A browser cannot honestly
// assemble any of them:
//   - transactions needs a real money ledger across five tables and a server-defined ET window;
//   - tax must be byte-identical to the annual-report PDF's lines, which are assembled (and
//     refund-netted) inside the report service;
//   - impact must exceed the read endpoint's hard `limit <= 50` ceiling, which the client cannot ask
//     past — a client-assembled version would silently be a top-50 titled "charities supported".
//
// So the honest division of labor is: the backend decides what the file contains, the client decides
// only what to name it and when to save it.

import { isGivmoError, readBinaryMeta, sdk } from '@givmo/api-client';
import { asSaveableBlob, binaryDownloadOptions, blobSave, type SaveFile } from './csv-export';

/** The result shape of a configured (non-throwing) SDK call with `responseStyle:'fields'`. */
interface BinaryResult {
  data?: unknown;
  error?: unknown;
  response?: Response;
}

/**
 * Run one server CSV export and save it.
 *
 * Throws on an errored or absent response so the caller toasts and NO phantom download fires — an
 * empty file landing in Downloads after a failed request reads as "the export was empty", which is a
 * different and wrong statement.
 *
 * The filename prefers the server's `Content-Disposition` (the backend stamps a period/year into it,
 * e.g. `company-tax-lines-2026.csv`) and falls back to a local stamp only when absent, so the file on
 * disk says what period it covers.
 *
 * `save` receives the finished file; it is `blobSave` unless the caller holds files for a click
 * (the Reports tiles, `csv-export.ts` § One save per interaction). The request and the bytes are the
 * same either way.
 */
async function runServerCsv(
  call: (options: typeof binaryDownloadOptions) => Promise<BinaryResult>,
  fallbackFilename: string,
  save: SaveFile = blobSave,
): Promise<void> {
  const result = await call(binaryDownloadOptions);
  if (result.error) throw result.error;
  if (!result.response) throw new Error('The export failed: no response.');

  const meta = readBinaryMeta(result.response);
  save(asSaveableBlob(result.data, 'text/csv'), meta.filename ?? fallbackFilename);
}

/**
 * `YYYY-MM-DD` for a Date in the LOCAL calendar — the shape the window params take.
 *
 * Explicitly NOT `toISOString().slice(0, 10)`, which is the UTC day. Pairing a UTC day with a
 * local-calendar year gave the window two ends drawn from two different calendars, and the two ways
 * that goes wrong are both silent:
 *   - in a UTC+ zone during the first hours of Jan 1 local, `from` had already rolled to the new year
 *     while `to` was still the old year's last UTC day — a backwards window the backend rejects;
 *   - in a US evening, `to` named tomorrow, so the file claimed a day that had not happened.
 */
function localIsoDay(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * The default transactions window: Jan 1 of the current year through today.
 *
 * Deliberately explicit rather than server-defaulted — the endpoint REQUIRES `from`/`to` precisely so
 * a returned file always answers a window someone asked for. The UI shows the dates it is sending.
 *
 * `from`'s year is sliced out of `to` rather than read separately, so the two ends cannot drift onto
 * different calendars again — `from <= to` now holds by construction, in every time zone.
 */
export function defaultTransactionsWindow(now: Date = new Date()): { from: string; to: string } {
  const today = localIsoDay(now);
  return { from: `${today.slice(0, 4)}-01-01`, to: today };
}

/** Download the company money-event ledger for `[from, to]` (inclusive ET days). */
export async function downloadTransactionsCsv(
  window: { from: string; to: string },
  save?: SaveFile,
): Promise<void> {
  await runServerCsv(
    (options) => sdk.companiesExportCompanyTransactionsCsv({ ...options, query: window }),
    `givmo_transactions_${window.from}_${window.to}.csv`,
    save,
  );
}

/** Download the Variant-A deductible tax lines for one calendar year. */
export async function downloadTaxLinesCsv(year: number, save?: SaveFile): Promise<void> {
  await runServerCsv(
    (options) => sdk.companiesExportCompanyTaxLinesCsv({ ...options, query: { year } }),
    `givmo_tax_lines_${year}.csv`,
    save,
  );
}

/**
 * Download every charity supported this year to date — uncapped.
 *
 * The endpoint takes no params (it decides the period), so the fallback name stamps the year the
 * request was made in. A bare `givmo_impact.csv` says nothing about what it covers, and two years of
 * them are indistinguishable in a Downloads folder — the same reason the tax and transactions
 * fallbacks carry their period.
 */
export async function downloadImpactCsv(now: Date = new Date(), save?: SaveFile): Promise<void> {
  await runServerCsv(
    (options) => sdk.companiesExportCompanyImpactCsv({ ...options }),
    `givmo_impact_${localIsoDay(now).slice(0, 4)}.csv`,
    save,
  );
}

/** A user-facing message for a failed export, preferring the backend's own error text. */
export function exportErrorMessage(error: unknown, noun: string): string {
  return isGivmoError(error)
    ? error.message
    : `Could not build the ${noun} export. Please try again.`;
}
